import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button, Dialog, useDialogStore } from '@ariakit/react';
import { Filter, X, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import type { Column, Table } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { multiSelect } from '../filters';
import {
  FilterStyle,
  DataTableColumn,
  AdvancedFilterRule,
  AdvancedFilterOperator,
  CompoundFilterValue,
} from '../types';
import moment from 'moment-timezone';

interface ColumnFilterProps<TData> {
  column: Column<TData>;
  table: Table<TData>;
  filterStyle?: FilterStyle;
}

const DATE_OPERATOR_LABELS: Record<AdvancedFilterOperator, string> = {
  dateRange: 'Date Range',
  dateYear: 'Year',
  dateBefore: 'Before',
  dateAfter: 'After',
};

const DATE_OPERATORS: AdvancedFilterOperator[] = ['dateRange', 'dateYear', 'dateBefore', 'dateAfter'];

function AdvancedRuleEditor({
  rule,
  availableYears,
  onChange,
  onRemove,
}: {
  rule: AdvancedFilterRule;
  availableYears: number[];
  onChange: (rule: AdvancedFilterRule) => void;
  onRemove: () => void;
}) {
  const handleOperatorChange = (operator: AdvancedFilterOperator) => {
    // Reset params when operator changes
    const defaultParams: Record<string, string | number> = {};
    if (operator === 'dateYear' && availableYears.length > 0) {
      defaultParams.year = availableYears[availableYears.length - 1];
    }
    onChange({ operator, params: defaultParams });
  };

  return (
    <div className="border rounded-md p-2 bg-gray-50 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <select
          value={rule.operator}
          onChange={(e) => handleOperatorChange(e.target.value as AdvancedFilterOperator)}
          className="flex-1 px-2 py-1 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {DATE_OPERATORS.map((op) => (
            <option key={op} value={op}>
              {DATE_OPERATOR_LABELS[op]}
            </option>
          ))}
        </select>
        <button
          onClick={onRemove}
          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded flex-shrink-0"
          title="Remove filter"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {rule.operator === 'dateRange' && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={(rule.params.from as string) || ''}
            onChange={(e) => onChange({ ...rule, params: { ...rule.params, from: e.target.value } })}
            className="flex-1 px-2 py-1 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-xs text-gray-500">to</span>
          <input
            type="date"
            value={(rule.params.to as string) || ''}
            onChange={(e) => onChange({ ...rule, params: { ...rule.params, to: e.target.value } })}
            className="flex-1 px-2 py-1 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      {rule.operator === 'dateYear' && (
        <select
          value={(rule.params.year as number) || ''}
          onChange={(e) => onChange({ ...rule, params: { year: Number(e.target.value) } })}
          className="w-full px-2 py-1 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {availableYears.length === 0 && (
            <option value="" disabled>No years available</option>
          )}
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      )}

      {rule.operator === 'dateBefore' && (
        <input
          type="date"
          value={(rule.params.date as string) || ''}
          onChange={(e) => onChange({ ...rule, params: { date: e.target.value } })}
          className="w-full px-2 py-1 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      )}

      {rule.operator === 'dateAfter' && (
        <input
          type="date"
          value={(rule.params.date as string) || ''}
          onChange={(e) => onChange({ ...rule, params: { date: e.target.value } })}
          className="w-full px-2 py-1 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      )}
    </div>
  );
}

function AdvancedRulePill({ rule }: { rule: AdvancedFilterRule }) {
  const label = DATE_OPERATOR_LABELS[rule.operator];
  let detail = '';

  switch (rule.operator) {
    case 'dateRange':
      detail = `${rule.params.from || '…'} → ${rule.params.to || '…'}`;
      break;
    case 'dateYear':
      detail = String(rule.params.year || '');
      break;
    case 'dateBefore':
      detail = String(rule.params.date || '');
      break;
    case 'dateAfter':
      detail = String(rule.params.date || '');
      break;
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">
      {label}: {detail}
    </span>
  );
}

export default function ColumnFilter<TData>({ column, table, filterStyle = 'select' }: ColumnFilterProps<TData>) {
  const dialog: any = useDialogStore();
  const [searchValue, setSearchValue] = useState('');
  const [selectedValues, setSelectedValues] = useState<Set<string> | null>(null);
  const [advancedRules, setAdvancedRules] = useState<AdvancedFilterRule[]>([]);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const columnDef = column.columnDef as DataTableColumn<unknown>;
  const hasAdvancedFilters = !!columnDef.columnFilterType;
  const maxAdvancedFilters = columnDef.maxAdvancedFilters ?? 1;
  const parseDateFn = columnDef.filterParseDateFn;
  const filterTimezone = columnDef.filterDateTimezone || 'UTC';

  // Extract available years from data for the year dropdown
  const availableYears = useMemo(() => {
    if (!hasAdvancedFilters || columnDef.columnFilterType !== 'date' || !parseDateFn) {
      return [];
    }
    const years = new Set<number>();
    const rows = table.getPreFilteredRowModel().rows;
    rows.forEach(row => {
      const rawValue = row.getValue(column.id);
      const m = parseDateFn(rawValue);
      if (m && m.isValid()) {
        years.add(m.tz(filterTimezone).year());
      }
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [hasAdvancedFilters, columnDef.columnFilterType, parseDateFn, filterTimezone, table, column.id]);
  
  const distinctValues = useMemo(() => {
    const values = new Map<string, number>();
    const displayFn = columnDef.filterDisplayFn;

    const toDisplayValue = (value: unknown): string => {
      if (value == null || String(value).trim() === '') return '(Blank)';
      return displayFn ? displayFn(value) : String(value);
    };
    
    // For select mode: use faceted values which respect other column filters
    // For solo-check mode: use all pre-filtered values
    if (filterStyle === 'select') {
      // Use faceted values - this automatically excludes this column's filter
      const faceted = column.getFacetedUniqueValues();
      faceted.forEach((count, value) => {
        const displayValue = toDisplayValue(value);
        values.set(displayValue, (values.get(displayValue) || 0) + count);
      });
    } else {
      // Solo-check mode: show all values regardless of filters
      const rowsToCheck = table.getPreFilteredRowModel().rows;
      rowsToCheck.forEach(row => {
        const value = row.getValue(column.id);
        const displayValue = toDisplayValue(value);
        values.set(displayValue, (values.get(displayValue) || 0) + 1);
      });
    }
    
    return Array.from(values.entries())
      .sort((a, b) => {
        if (a[0] === '(Blank)') return 1;
        if (b[0] === '(Blank)') return -1;
        return a[0].localeCompare(b[0]);
      });
  }, [column, table, filterStyle, column.getFacetedUniqueValues()]);
  
  const filteredValues = useMemo(() => {
    if (!searchValue) return distinctValues;
    
    const search = searchValue.toLowerCase();
    return distinctValues.filter(([value]) => 
      value.toLowerCase().includes(search)
    );
  }, [distinctValues, searchValue]);
  
  // Reset search and initialize state when dialog opens
  useEffect(() => {
    if (dialog.open) {
      setSearchValue('');

      // Initialize advanced rules from current filter value
      if (hasAdvancedFilters) {
        const currentFilter = column.getFilterValue();
        if (currentFilter && typeof currentFilter === 'object' && !Array.isArray(currentFilter)) {
          const compound = currentFilter as CompoundFilterValue;
          setAdvancedRules(compound.advancedRules || []);
          setAdvancedExpanded((compound.advancedRules || []).length > 0);
        } else {
          setAdvancedRules([]);
          setAdvancedExpanded(false);
        }
      }
    }
  }, [dialog.open]);

  // Initialize selected values when we have distinct values
  useEffect(() => {
    if (distinctValues.length === 0) return;
    if (selectedValues !== null) return; // Already initialized
    
    const currentFilter = column.getFilterValue();
    
    if (hasAdvancedFilters && currentFilter && typeof currentFilter === 'object' && !Array.isArray(currentFilter)) {
      // CompoundFilterValue
      const compound = currentFilter as CompoundFilterValue;
      if (Array.isArray(compound.selectedValues)) {
        setSelectedValues(new Set(compound.selectedValues));
      } else {
        setSelectedValues(new Set(distinctValues.map(([value]) => value)));
      }
    } else if (Array.isArray(currentFilter)) {
      // Legacy: plain string array
      setSelectedValues(new Set(currentFilter as string[]));
    } else {
      // No filter (undefined) means all values should be selected
      setSelectedValues(new Set(distinctValues.map(([value]) => value)));
    }
  }, [distinctValues, column, selectedValues]);
  
  const handleSelectAll = () => {
    if (filterStyle === 'select') {
      setSelectedValues(new Set(filteredValues.map(([value]) => value)));
    } else {
      setSelectedValues(new Set(distinctValues.map(([value]) => value)));
    }
  };
  
  const handleSelectNone = () => {
    if (filterStyle === 'select') {
      const currentSelected = selectedValues || new Set(distinctValues.map(([v]) => v));
      const newSelected = new Set(currentSelected);
      filteredValues.forEach(([value]) => {
        newSelected.delete(value);
      });
      setSelectedValues(newSelected);
    } else {
      setSelectedValues(new Set());
    }
  };

  const handleToggleValue = (value: string) => {
    // Initialize if needed
    const currentSelected = selectedValues || new Set(distinctValues.map(([v]) => v));
    const newSelected = new Set(currentSelected);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    setSelectedValues(newSelected);
  };

  const handleAddRule = () => {
    const defaultOperator: AdvancedFilterOperator = 'dateRange';
    const defaultParams: Record<string, string | number> = {};
    setAdvancedRules(prev => [...prev, { operator: defaultOperator, params: defaultParams }]);
    setAdvancedExpanded(true);
  };

  const handleUpdateRule = (index: number, rule: AdvancedFilterRule) => {
    setAdvancedRules(prev => prev.map((r, i) => (i === index ? rule : r)));
  };

  const handleRemoveRule = (index: number) => {
    setAdvancedRules(prev => prev.filter((_, i) => i !== index));
  };

  // Check if an advanced rule has meaningful params set
  const isRuleActive = (rule: AdvancedFilterRule): boolean => {
    switch (rule.operator) {
      case 'dateRange':
        return !!(rule.params.from || rule.params.to);
      case 'dateYear':
        return rule.params.year !== undefined && rule.params.year !== '';
      case 'dateBefore':
      case 'dateAfter':
        return !!rule.params.date;
      default:
        return false;
    }
  };
  
  const handleApply = () => {
    // Get the actual selected values (initialize if needed)
    const currentSelected = selectedValues || new Set(distinctValues.map(([v]) => v));

    // Determine value-selection
    let valuesToApply: string[] | undefined;

    if (currentSelected.size === 0) {
      valuesToApply = [];
    } else if (filterStyle === 'solo-check' && currentSelected.size === distinctValues.length) {
      valuesToApply = undefined;
    } else if (filterStyle === 'select') {
      const search = searchValue.toLowerCase();
      if (!search) {
        valuesToApply = Array.from(currentSelected);
      } else {
        valuesToApply = Array.from(currentSelected).filter((value: string) => value.toLowerCase().includes(search));
      }
    } else {
      valuesToApply = Array.from(currentSelected);
    }

    // If advanced filters are enabled, build CompoundFilterValue
    if (hasAdvancedFilters) {
      const activeRules = advancedRules.filter(isRuleActive);
      const allValuesSelected = valuesToApply === undefined ||
        (valuesToApply && valuesToApply.length === distinctValues.length);

      // If no rules and all values selected, clear the filter entirely
      if (activeRules.length === 0 && allValuesSelected) {
        column.setFilterValue(undefined);
      } else {
        const compoundValue: CompoundFilterValue = {};
        if (valuesToApply !== undefined) {
          compoundValue.selectedValues = valuesToApply;
        }
        if (activeRules.length > 0) {
          compoundValue.advancedRules = activeRules;
        }
        column.setFilterValue(compoundValue);
      }
    } else {
      // Legacy behavior for columns without advanced filters
      column.setFilterValue(valuesToApply);
    }

    dialog.hide();
  };
  
  const handleClear = () => {
    setSearchValue("");
    setAdvancedRules([]);
    if (filterStyle === 'select') {
      // In select mode, clearing means selecting all currently visible values
      const allVisibleValues = distinctValues.map(([value]) => value);
      column.setFilterValue(undefined); // Clear filter to show all
      setSelectedValues(new Set(allVisibleValues));
    } else {
      // In solo-check mode, clearing means removing the filter entirely
      column.setFilterValue(undefined);
      setSelectedValues(new Set(distinctValues.map(([value]) => value)));
    }
    dialog.hide();
  };
  
  const isFiltered = column.getIsFiltered();
  
  // Virtual scrolling setup
  const virtualizer = useVirtualizer({
    count: filteredValues.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40, // Estimated height of each checkbox item
    overscan: 5,
  });
  
  return (
    <>
      <Button
        onClick={dialog.toggle}
        className={`p-1 rounded hover:bg-gray-100 ${isFiltered ? 'text-blue-600' : 'text-gray-400'}`}
        aria-label={`Filter ${column.id}`}
        title="Click to filter"
      >
        <Filter className="h-4 w-4" />
      </Button>
      
      <Dialog
        initialFocus={inputRef}
        store={dialog}
        className="fixed inset-4 z-50 flex items-center justify-center"
        backdrop={<div className="fixed inset-0 bg-black/30" />}
        data-testid="filter-dialog"
      >
        <div className="bg-white rounded-lg shadow-xl w-96 max-h-[600px] flex flex-col">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Filter: {column.id}</h2>
            <Button
              onClick={dialog.hide}
              className="p-1 rounded hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Advanced filters section */}
          {hasAdvancedFilters && (
            <div className="px-4 py-2 border-b">
              <button
                onClick={() => setAdvancedExpanded(!advancedExpanded)}
                className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <span className="flex items-center gap-1.5">
                  Date Filters
                  {advancedRules.filter(isRuleActive).length > 0 && (
                    <span className="inline-flex items-center justify-center w-4 h-4 text-xs bg-blue-100 text-blue-700 rounded-full">
                      {advancedRules.filter(isRuleActive).length}
                    </span>
                  )}
                </span>
                {advancedExpanded
                  ? <ChevronUp className="h-4 w-4 text-gray-400" />
                  : <ChevronDown className="h-4 w-4 text-gray-400" />
                }
              </button>

              {!advancedExpanded && advancedRules.filter(isRuleActive).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {advancedRules.filter(isRuleActive).map((rule, index) => (
                    <AdvancedRulePill key={index} rule={rule} />
                  ))}
                </div>
              )}

              {advancedExpanded && (
                <div className="mt-2 space-y-2">
                  {advancedRules.map((rule, index) => (
                    <AdvancedRuleEditor
                      key={index}
                      rule={rule}
                      availableYears={availableYears}
                      onChange={(updated) => handleUpdateRule(index, updated)}
                      onRemove={() => handleRemoveRule(index)}
                    />
                  ))}
                  {advancedRules.length < maxAdvancedFilters && (
                    <button
                      onClick={handleAddRule}
                      className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add filter
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="px-4 py-3 border-b">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search values..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="px-4 py-2 border-b flex gap-2">
            <button
              onClick={handleSelectAll}
              className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
            >
              Select All
            </button>
            <button
              onClick={handleSelectNone}
              className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
            >
              Clear All
            </button>
          </div>
          
          <div 
            ref={parentRef}
            className="flex-1 overflow-auto px-4 py-2 min-w-0"
            style={{ maxHeight: '400px' }}
          >
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const [value, count] = filteredValues[virtualItem.index];
                return (
                  <div
                    key={virtualItem.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer min-w-0">
                      <input
                        type="checkbox"
                        checked={
                          selectedValues === null 
                            ? (column.getFilterValue() === undefined || (Array.isArray(column.getFilterValue()) && (column.getFilterValue() as string[]).includes(value)))
                            : selectedValues.has(value)
                        }
                        onChange={() => handleToggleValue(value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                      />
                      <span 
                        className="flex-1 truncate min-w-0 max-w-full"
                        title={value}
                      >
                        {value}
                      </span>
                      <span 
                        className="text-sm text-gray-500 flex-shrink-0 ml-2"
                        title={`Count: ${count}`}
                      >
                        ({count})
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="px-4 py-3 border-t">
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleClear}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Clear Filter
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
